/**
 * useAuthProviderSettings Hook
 * Main orchestration hook for authentication provider settings
 */

import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/index';
import { log } from '../../utils/errors/logger';
import { useSnackbar } from '../../contexts/SnackbarContext';
import type { AuthProviderSettingsProps } from '../../components/system/AuthProviderSettings/types';
import type { AuthProviderType } from '../../api/data-contracts';
import { useAuthProviderList } from './useAuthProviderList';
import { useAuthProviderForm } from './useAuthProviderForm';
import { useProviderTemplates } from '../../components/system/AuthProviderSettings/components/ProviderTemplates';

/**
 * Main hook for managing authentication provider settings
 * Orchestrates list and form management
 */
export const useAuthProviderSettings = (props: AuthProviderSettingsProps) => {
  const { onUpdate } = props;
  const { t } = useTranslation();
  const { showSuccess } = useSnackbar();
  const templates = useProviderTemplates();

  // Provider list management
  const {
    providers,
    primaryProvider,
    loading: listLoading,
    loadProviders,
    deleteProvider: handleDeleteProvider,
    toggleProvider: handleToggleProvider,
    setPrimaryProvider: handleSetPrimary,
  } = useAuthProviderList(onUpdate);

  // Form management
  const {
    form,
    dialogOpen,
    editingProvider,
    error,
    handleOpenAdd,
    handleOpenEdit,
    handleCloseDialog,
    handleFieldChange,
    handleProviderTypeChange,
    setError,
  } = useAuthProviderForm();

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // Delete provider with confirmation
  const deleteProvider = useCallback(
    async (provider: typeof providers[0]) => {
      const template = templates[provider.provider_type];
      const providerName = template?.name || provider.provider_type;

      if (!window.confirm(t('auth.providers.deleteConfirm', { provider: providerName }))) {
        return;
      }

      await handleDeleteProvider(provider);
    },
    [templates, t, handleDeleteProvider]
  );

  // Save provider (create or update)
  const handleSaveProvider = useCallback(async () => {
    try {
      setError(null);

      // Validate required fields
      const template = templates[form.provider_type];
      const requiredFields = template?.fields.filter((f) => f.required) || [];

      for (const field of requiredFields) {
        if (!form.config[field.key as keyof typeof form.config]) {
          throw new Error(t('auth.providers.fieldRequired', { field: field.label }));
        }
      }

      // Call API to save provider
      log.user.action('save_auth_provider', 'AuthProviderSettings', {
        providerType: form.provider_type,
        isEdit: Boolean(editingProvider),
      });

      if (editingProvider) {
        await apiClient.updateAuthProviderApiAuthProvidersProviderTypePut(
          form.provider_type as AuthProviderType,
          {
            config: form.config,
          }
        );
      } else {
        await apiClient.createAuthProviderApiAuthProvidersPost({
          provider_type: form.provider_type as AuthProviderType,
          is_enabled: true,
          is_primary: false,
          config: form.config,
        });
      }

      // Reload providers
      await loadProviders();
      handleCloseDialog();

      if (onUpdate) {
        onUpdate();
      }

      log.info('Successfully saved auth provider', { providerType: form.provider_type });
      showSuccess(t('auth.providers.saved'));
    } catch (err) {
      log.error('Failed to save auth provider', {
        providerType: form.provider_type,
        error: err,
      });
      setError(err instanceof Error ? err.message : t('auth.providers.failedToSave'));
    }
  }, [
    form,
    templates,
    editingProvider,
    t,
    setError,
    onUpdate,
    loadProviders,
    handleCloseDialog,
    showSuccess,
  ]);

  return {
    // Provider list
    providers,
    primaryProvider,
    loading: listLoading,
    templates,

    // Form state
    form,
    dialogOpen,
    editingProvider,
    error,

    // Actions
    handleOpenAdd,
    handleOpenEdit,
    handleCloseDialog,
    handleFieldChange,
    handleProviderTypeChange,
    handleSaveProvider,
    deleteProvider,
    toggleProvider: handleToggleProvider,
    setPrimaryProvider: handleSetPrimary,
  };
};
